import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import api, {
  setSudoToken, clearSudoToken, getSudoToken,
  registerPinPromptHandler, registerPinSetupHandler,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PinDialog from "@/components/security/PinDialog";
import PinSetupWizard from "@/components/security/PinSetupWizard";
import PinRecoveryDialog from "@/components/security/PinRecoveryDialog";

const PinContext = createContext(null);

/**
 * Global PIN Security Manager.
 *
 * Any code — inside or outside React — can request PIN authorisation via:
 *
 *   const pin = usePinSecurity();
 *   await pin.require("appointment.delete");
 *   // ...proceed with the sensitive call
 *
 * Additionally, protected API calls are auto-guarded: when the backend
 * returns 440 the axios interceptor asks this provider to prompt the
 * user, then transparently retries the original request. Feature
 * components normally don't need to call `require` themselves.
 */
export function PinProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null); // { configured, ...}
  const [sudoExpiresAt, setSudoExpiresAt] = useState(0);

  // Dialog control state
  const [verify, setVerify] = useState({ open: false, operation: "" });
  const [wizardOpen, setWizardOpen] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);

  // pending promises to resolve when the verify/setup flow completes
  const verifyResolverRef = useRef(null);
  const setupResolverRef = useRef(null);

  const refreshStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/security/status");
      setStatus(data);
      return data;
    } catch (err) {
      // 401 handled globally
      return null;
    }
  }, []);

  // Reload status on login / user change
  useEffect(() => {
    if (!user) {
      setStatus(null);
      clearSudoToken();
      setSudoExpiresAt(0);
      return;
    }
    refreshStatus();
  }, [user, refreshStatus]);

  // Auto-open setup wizard when PIN is not configured
  useEffect(() => {
    if (user && status && !status.configured) setWizardOpen(true);
    if (status && status.configured) setWizardOpen(false);
  }, [user, status]);

  // Tick every 30s to expire the sudo token in UI state
  useEffect(() => {
    const id = setInterval(() => {
      if (sudoExpiresAt && Date.now() >= sudoExpiresAt) {
        clearSudoToken();
        setSudoExpiresAt(0);
      }
    }, 30_000);
    return () => clearInterval(id);
  }, [sudoExpiresAt]);

  // ---------- Verify dialog ----------
  const openVerify = useCallback((operation) => {
    return new Promise((resolve) => {
      verifyResolverRef.current = resolve;
      setVerify({ open: true, operation: operation || "" });
    });
  }, []);

  const closeVerify = (token = null) => {
    setVerify({ open: false, operation: "" });
    const r = verifyResolverRef.current;
    verifyResolverRef.current = null;
    if (r) r(token);
  };

  const onVerifySuccess = (token, expiresAtIso) => {
    setSudoToken(token, expiresAtIso);
    setSudoExpiresAt(new Date(expiresAtIso).getTime());
    closeVerify(token);
  };

  // ---------- Setup wizard ----------
  const openSetup = useCallback(() => {
    return new Promise((resolve) => {
      setupResolverRef.current = resolve;
      setWizardOpen(true);
    });
  }, []);

  const onSetupDone = async () => {
    setWizardOpen(false);
    await refreshStatus();
    const r = setupResolverRef.current;
    setupResolverRef.current = null;
    if (r) r(true);
  };

  // ---------- Public API ----------
  const require = useCallback(async (operation) => {
    // Already got a valid sudo token? Fast path.
    const existing = getSudoToken();
    if (existing) return existing;
    // If not configured, force setup first
    if (status && !status.configured) {
      const done = await openSetup();
      if (!done) throw new Error("PIN no configurado");
      // After setup the user is still not authenticated for sudo — ask for PIN
    }
    const token = await openVerify(operation);
    if (!token) throw new Error("PIN requerido");
    return token;
  }, [status, openSetup, openVerify]);

  const value = useMemo(() => ({
    status,
    refreshStatus,
    require,
    openChange: () => setChangeOpen(true),
    openRecovery: () => setRecoveryOpen(true),
    sudoExpiresAt,
    hasSudo: () => !!getSudoToken(),
  }), [status, refreshStatus, require, sudoExpiresAt]);

  // Wire the axios interceptor to open our dialogs
  useEffect(() => {
    registerPinPromptHandler(async (operation) => {
      try { return await openVerify(operation); } catch { return null; }
    });
    registerPinSetupHandler(async () => {
      try { return await openSetup(); } catch { return false; }
    });
    return () => {
      registerPinPromptHandler(null);
      registerPinSetupHandler(null);
    };
  }, [openVerify, openSetup]);

  // Change dialog is separate (lazy state)
  const [changeOpen, setChangeOpen] = useState(false);

  return (
    <PinContext.Provider value={value}>
      {children}

      {/* Setup wizard — modal, cannot be dismissed */}
      {user && (
        <PinSetupWizard open={wizardOpen} onDone={onSetupDone} />
      )}

      {/* Verify dialog */}
      {user && (
        <PinDialog
          open={verify.open}
          operation={verify.operation}
          onClose={() => closeVerify(null)}
          onSuccess={onVerifySuccess}
          onRequestRecovery={() => { closeVerify(null); setRecoveryOpen(true); }}
        />
      )}

      {/* Recovery dialog */}
      {user && (
        <PinRecoveryDialog
          open={recoveryOpen}
          onClose={() => setRecoveryOpen(false)}
          onDone={() => { setRecoveryOpen(false); refreshStatus(); }}
        />
      )}

      {/* Change dialog — lazy import to avoid circular */}
      {user && changeOpen && (
        <LazyChangeDialog
          onClose={() => setChangeOpen(false)}
          onDone={() => { setChangeOpen(false); refreshStatus(); }}
        />
      )}
    </PinContext.Provider>
  );
}

function LazyChangeDialog({ onClose, onDone }) {
  // Small local wrapper so the change dialog only loads when opened.
  const [Mod, setMod] = useState(null);
  useEffect(() => {
    import("@/components/security/PinChangeDialog").then((m) => setMod(() => m.default));
  }, []);
  if (!Mod) return null;
  return <Mod open={true} onClose={onClose} onDone={onDone} />;
}

export const usePinSecurity = () => {
  const ctx = useContext(PinContext);
  if (!ctx) throw new Error("usePinSecurity must be used within PinProvider");
  return ctx;
};
